import * as React from "react";
import * as ReactDOM from "react-dom/client";
import reportWebVitals from "./reportWebVitals";

// note: router provider
import { RouterProvider } from "react-router-dom";

// note: import router
import router from "./App.js";

// note: css core
import "./index.css";

// note: redux
import { configureStore } from "@reduxjs/toolkit";
import { Provider } from "react-redux";
import { combineReducers } from "redux";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { PersistGate } from "redux-persist/integration/react";

// note: reducer
import authReducer from "state/reducer/auth";
import menuReducer from "state/reducer/menu";
import userReducer from "state/reducer/user";
import verifyReducer from "state/reducer/verify";
import propertyReducer from "state/reducer/property";
import buildingReducer from "state/reducer/building";
import departmentReducer from "state/reducer/department";
import tenantReducer from "state/reducer/tenant";
import unitReducer from "state/reducer/unit";
import agreementReducer from "state/reducer/agreement";
import billingReducer from "state/reducer/billing";
import complaintReducer from "state/reducer/complaint";
import utilityReducer from "state/reducer/utility";
import eventReducer from "state/reducer/event";
import floorReducer from "state/reducer/floor";
import leaseAgreementReducer from "state/reducer/lease_agreement";
import contractReducer from "state/reducer/contract";
import negotiationReducer from "state/reducer/negotiation";
import unitTypeReducer from "state/reducer/unit_type";
import invoiceReducer from "state/reducer/invoice";
import paymentReducer from "state/reducer/payment";
import handOverReducer from "state/reducer/handover";
import mitraReducer from "state/reducer/mitra";
import quotationReducer from "state/reducer/quotation";
import historyReducer from "state/reducer/history";
import pksReducer from "state/reducer/pks";
import serviceTypeReducer from "state/reducer/service_type";
import serviceReducer from "state/reducer/service";
import directorateReducer from "state/reducer/directorate";
import divisionReducer from "state/reducer/division";
import permissionReducer from "state/reducer/permission";
import cashFlowReducer from "state/reducer/cash_flow";
import revenueRecognationReducer from "state/reducer/revenue_recognation";
import basePriceReducer from "state/reducer/base_price";
import notificationReducer from "state/reducer/notification";
import roleReducer from "state/reducer/role";
import companyReducer from "state/reducer/company";
import workingUnitReducer from "state/reducer/working_unit";
import revenueReducer from "state/reducer/revenue";
import verificationReducer from "state/reducer/verification";
import billingCycleReducer from "state/reducer/billing";
import billingStatementReducer from "state/reducer/billing_statement";
import salesRecapReducer from "state/reducer/sales_recap";
import utilityReadingReducer from "state/reducer/utility_reading";
import addendumReducer from "state/reducer/addendum";
import warningLetterReducer from "state/reducer/warning_letter";
import penaltyReducer from "state/reducer/penalty";
import utilityRecordReducer from "state/reducer/utility_record";
import contractNewReducer from "state/reducer/contract_new";
import costComponentReducer from "state/reducer/cost_component";
import offeringReducer from "state/reducer/offering";
import contractVersionReducer from "state/reducer/contract_version";
import contractInvoiceReducer from "state/reducer/contract_invoice";
import createOfferingFormReducer from "state/reducer/create_offering_form";

// note: component import
// import { Theme } from "components/Reusable/Themes";
import { IntlProvider } from "react-intl";
import useConfig from "hooks/useConfig";
import ThemeCustomization from "themes";
import { CASLProvider } from "contexts/CASLContext";
import { injectStore } from "config/axiosInstance";

// note: combine reducer
const appReducers = combineReducers({
  auth: authReducer,
  menu: menuReducer,
  user: userReducer,
  verify: verifyReducer,
  property: propertyReducer,
  building: buildingReducer,
  department: departmentReducer,
  tenant: tenantReducer,
  unit: unitReducer,
  agreement: agreementReducer,
  billing: billingReducer,
  complaints: complaintReducer,
  utility: utilityReducer,
  event: eventReducer,
  floor: floorReducer,
  leaseAgreement: leaseAgreementReducer,
  contract: contractReducer,
  negotiation: negotiationReducer,
  unit_type: unitTypeReducer,
  invoice: invoiceReducer,
  payment: paymentReducer,
  handover: handOverReducer,
  mitra: mitraReducer,
  quotation: quotationReducer,
  history: historyReducer,
  pks: pksReducer,
  service: serviceReducer,
  serviceType: serviceTypeReducer,
  directorate: directorateReducer,
  division: divisionReducer,
  permission: permissionReducer,
  cashFlow: cashFlowReducer,
  revenueRecognation: revenueRecognationReducer,
  base_price: basePriceReducer,
  notification: notificationReducer,
  role: roleReducer,
  company: companyReducer,
  working_unit: workingUnitReducer,
  revenue: revenueReducer,
  verification: verificationReducer,
  billingCycle: billingCycleReducer,
  billingStatement: billingStatementReducer,
  salesRecap: salesRecapReducer,
  utilityReading: utilityReadingReducer,
  utilityRecord: utilityRecordReducer,
  addendum: addendumReducer,
  warningLetter: warningLetterReducer,
  penalty: penaltyReducer,
  contractNew: contractNewReducer,
  costComponent: costComponentReducer,
  offering: offeringReducer,
  contractVersion: contractVersionReducer,
  contractInvoice: contractInvoiceReducer,
  createOfferingForm: createOfferingFormReducer,
});

// note: wipe every slice on logout (success or failure) so the next user on this
// note: browser never sees the previous user's persisted data.
const rootReducers = (state, action) =>
  appReducers(
    action.type === "logout/fulfilled" || action.type === "logout/rejected"
      ? undefined
      : state,
    action,
  );

// note: redux persist config
const persistConfig = {
  key: "tenant",
  storage,
  // note: only slices that must survive a reload. everything else is API data
  // note: that pages refetch, so keeping it in localStorage only leaks/stales it.
  whitelist: [
    // core: session, drawer, profile (profile is fetched only at login; drives x-company-id + CASL)
    "auth",
    "menu",
    "user",
    // multi-step form drafts
    "createOfferingForm",
    "contractNew", // also selectedContract for /contracts-management/detail|versions
    // ponytail: detail pages below still read the `selected*` item from redux, because be-ar has no
    // get-by-id endpoint for them yet. drop each once its page fetches by ?id= (like /mitra/detail).
    "billingCycle", // selectedCycle -> /billing/cycles/details (no /billing-cycle route in be-ar at all)
    "salesRecap", // selectedRecap -> /sales-recap/detail (no GET /contract-sales-recap/:scheduleId)
  ],
};

// note: redux persist reducer
const persistedReducer = persistReducer(persistConfig, rootReducers);

// note: redux store
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // note: the script does not use temporarily.
      // serializableCheck: {
      //   ignoreActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      // },
      serializableCheck: false,
      immutableCheck: { warnAfter: 128 },
    }),
});

injectStore(store);

const messages = {
  pages: "Pages",
  dashboard: "Dashboard",
  mitra: "Mitra",
  "my unit": "My Unit",
  offering: "Offering",
  "kerja sama": "Kerja Sama",
  "proforma invoice": "Proforma Invoice",
  properties: "Properties",
  property: "Property",
  building: "Building",
  floor: "Floor",
  unit: "Unit",
  "organizational structure": "Organizational structure",
  settings: "Settings",
  users: "Users",
  "harga dasar gedung": "Harga Dasar Gedung",
  permissions: "Permission",
  service: "Service",
  tenant: "Tenant",
  "more-items": "More items",
  "disabled-menu": "Disabled menu",
  documentation: "Documentation",
  "contact-us": "Contact us",
  ticket: "Ticket",
  payment: "Payment",
  utility: "Utility",
  report: "Report",
  "cash flow": "Cash Flow",
  "revenue recognation": "Revenue Recognition",
  company: "Company",
  roles: "Roles",
  "working unit": "Working Unit",
  "master data": "Master Data",
  "admin settings": "Admin Settings",
  "utility pages": "Utility Pages",
  "sales recap": "Sales Recap",
  "base price": "Base Price",
  "utility pages": "Utility",
  penalty: "Penalty",
  "warning letter": "Warning Letter",
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistStore(store)}>
        <CASLProvider>
          <IntlProvider messages={messages} locale="en">
            <ThemeCustomization>
              <RouterProvider router={router} />
            </ThemeCustomization>
          </IntlProvider>
        </CASLProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
);

// note: If you want to start measuring performance in your app, pass a function
// note: to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
